import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { getMySubscription, getMyUsage } from "@/lib/payments/payments.functions";
import { getPaddleEnvironment } from "@/lib/paddle";
import { tierFromProductId, TIERS, type TierLimits } from "@/lib/payments/tiers";
import { useAuth } from "@/lib/auth/AuthContext";
import { subscribeToTable } from "@/lib/realtime";

export type SubState = {
  loading: boolean;
  isActive: boolean;
  tier: TierLimits | null;
  productId: string | null;
  status: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  usage: { scans_used: number; ai_queries_used: number; dossiers_used: number } | null;
  refresh: () => Promise<void>;
};

export function useSubscription(): SubState {
  const { session, isAdmin } = useAuth();
  const fetchSub = useServerFn(getMySubscription);
  const fetchUsage = useServerFn(getMyUsage);
  const env = getPaddleEnvironment();
  const [state, setState] = useState<Omit<SubState, "refresh">>({
    loading: true,
    isActive: false,
    tier: null,
    productId: null,
    status: null,
    currentPeriodEnd: null,
    cancelAtPeriodEnd: false,
    usage: null,
  });

  async function load() {
    if (!session) {
      setState((s) => ({ ...s, loading: false }));
      return;
    }
    try {
      const [sub, usage] = await Promise.all([
        fetchSub({ data: { environment: env } }),
        fetchUsage(),
      ]);
      const status = sub?.status ?? null;
      const cpe = sub?.current_period_end ?? null;
      const active =
        isAdmin ||
        (!!sub &&
          (
            // past_due / paused lose access until payment succeeds.
            (["active", "trialing"].includes(status ?? "") &&
              (!cpe || new Date(cpe) > new Date())) ||
            (status === "canceled" && !!cpe && new Date(cpe) > new Date())
          ));
      const resolvedTier =
        tierFromProductId(sub?.product_id ?? null) ??
        (isAdmin ? TIERS.command_plan : null);
      setState({
        loading: false,
        isActive: active,
        tier: resolvedTier,
        productId: sub?.product_id ?? (isAdmin ? "command_plan" : null),
        status,
        currentPeriodEnd: cpe,
        cancelAtPeriodEnd: sub?.cancel_at_period_end ?? false,
        usage: usage as any,
      });
    } catch {
      setState((s) => ({ ...s, loading: false }));
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.user?.id, isAdmin]);

  useEffect(() => {
    const uid = session?.user?.id;
    if (!uid) return;
    return subscribeToTable(
      `sub-${uid}`,
      { event: "*", schema: "public", table: "subscriptions", filter: `user_id=eq.${uid}` },
      () => void load(),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.user?.id]);

  return { ...state, refresh: load };
}